import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SeoHead from '../../components/feature/SeoHead';

const DevisPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    adresse: '',
    ville: '',
    codePostal: '',
    typeVehicule: '',
    marque: '',
    modele: '',
    prestations: [] as string[],
    datePreferee: '',
    heurePreferee: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const prestationsOptions = [
    'Nettoyage Express',
    'Nettoyage Approfondi',
    'Nettoyage Suprême',
    'Polish & Correction',
    'Céramique',
    'Ciel Étoilé',
    'Autre (préciser dans le message)',
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePrestationToggle = (prestation: string) => {
    setFormData(prev => ({
      ...prev,
      prestations: prev.prestations.includes(prestation)
        ? prev.prestations.filter(p => p !== prestation)
        : [...prev.prestations, prestation],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('nom', formData.nom);
      formBody.append('prenom', formData.prenom);
      formBody.append('email', formData.email);
      formBody.append('telephone', formData.telephone);
      formBody.append('adresse', formData.adresse);
      formBody.append('ville', formData.ville);
      formBody.append('codePostal', formData.codePostal);
      formBody.append('typeVehicule', formData.typeVehicule);
      formBody.append('marque', formData.marque);
      formBody.append('modele', formData.modele);
      formBody.append('prestations', formData.prestations.join(', '));
      formBody.append('datePreferee', formData.datePreferee);
      formBody.append('heurePreferee', formData.heurePreferee);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d5hui9r48noljcu3pu90', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        // ✅ Envoyer les emails (admin + client)
        try {
          await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-devis-notification`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
            },
            body: JSON.stringify({
              nom: formData.nom,
              prenom: formData.prenom,
              email: formData.email,
              telephone: formData.telephone,
              adresse: formData.adresse,
              ville: formData.ville,
              codePostal: formData.codePostal,
              typeVehicule: formData.typeVehicule,
              marque: formData.marque,
              modele: formData.modele,
              prestations: formData.prestations.join(', '),
              datePreferee: formData.datePreferee,
              heurePreferee: formData.heurePreferee,
              message: formData.message,
            }),
          });
          console.log('✅ Emails envoyés (admin + client)');
        } catch (emailError) {
          console.error('❌ Erreur envoi emails:', emailError);
        }

        setSubmitStatus('success');
        setFormData({
          nom: '',
          prenom: '',
          email: '',
          telephone: '',
          adresse: '',
          ville: '',
          codePostal: '',
          typeVehicule: '',
          marque: '',
          modele: '',
          prestations: [],
          datePreferee: '',
          heurePreferee: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.louatiautocenter.ch';

  return (
    <div className="min-h-screen bg-dark-bg">
      <SeoHead
        title="Devis Gratuit Esthétique Automobile à Domicile | Louati AUTOCENTER Payerne"
        description="Demandez un devis gratuit pour votre prestation d'esthétique automobile à domicile à Payerne. Nettoyage, polish, céramique. Réponse sous 24h. Service à domicile disponible."
        keywords="devis detailing Payerne, devis lavage auto domicile, devis céramique voiture Suisse"
        canonical={`${siteUrl}/devis`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Devis Gratuit Esthétique Automobile à Domicile | Louati AUTOCENTER Payerne",
          "description": "Demandez un devis gratuit pour votre prestation d'esthétique automobile à domicile à Payerne.",
          "url": `${siteUrl}/devis`,
          "inLanguage": "fr-CH",
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Accueil", "item": `${siteUrl}/` },
              { "@type": "ListItem", "position": 2, "name": "Devis", "item": `${siteUrl}/devis` }
            ]
          }
        }}
      />
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/b6f5656f9ce267b3e1f18aa57ae82767.jpg"
            alt="Service à domicile"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-dark-bg"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6">
            Demande de Devis
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto mb-8">
            Service à domicile personnalisé - Nous venons à vous
          </p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-3 border border-white/30 rounded-lg font-semibold hover:bg-white/10 transition-colors whitespace-nowrap text-white cursor-pointer"
          >
            Retour
          </button>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 bg-dark-bg">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          {/* Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="bg-dark-card border border-dark-border rounded-xl p-6 text-center">
              <div className="w-12 h-12 flex items-center justify-center bg-primary/10 rounded-full mx-auto mb-4">
                <i className="ri-home-smile-line text-2xl text-primary"></i>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">À votre domicile</h3>
              <p className="text-sm text-gray-400">Nous venons chez vous</p>
            </div>

            <div className="bg-dark-card border border-dark-border rounded-xl p-6 text-center">
              <div className="w-12 h-12 flex items-center justify-center bg-accent-sky/10 rounded-full mx-auto mb-4">
                <i className="ri-time-line text-2xl text-accent-sky"></i>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Réponse rapide</h3>
              <p className="text-sm text-gray-400">Sous 24h ouvrées</p>
            </div>

            <div className="bg-dark-card border border-dark-border rounded-xl p-6 text-center">
              <div className="w-12 h-12 flex items-center justify-center bg-accent-gold/10 rounded-full mx-auto mb-4">
                <i className="ri-file-list-3-line text-2xl text-accent-gold"></i>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Devis gratuit</h3>
              <p className="text-sm text-gray-400">Sans engagement</p>
            </div>
          </div>

          {/* Form */}
          <div className="bg-dark-card border border-dark-border rounded-2xl p-8 md:p-12">
            <form id="devis-form" data-readdy-form onSubmit={handleSubmit} className="space-y-8">
              {/* Informations personnelles */}
              <div>
                <h2 className="text-2xl font-bold text-primary mb-6">Vos informations</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="prenom" className="block text-sm font-medium text-gray-300 mb-2">
                      Prénom *
                    </label>
                    <input
                      type="text"
                      id="prenom"
                      name="prenom"
                      value={formData.prenom}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-dark-bg border border-dark-border rounded-lg text-white text-sm focus:outline-none focus:border-primary transition-colors"
                      placeholder="Votre prénom"
                    />
                  </div>

                  <div>
                    <label htmlFor="nom" className="block text-sm font-medium text-gray-300 mb-2">
                      Nom *
                    </label>
                    <input
                      type="text"
                      id="nom"
                      name="nom"
                      value={formData.nom}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-dark-bg border border-dark-border rounded-lg text-white text-sm focus:outline-none focus:border-primary transition-colors"
                      placeholder="Votre nom"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-dark-bg border border-dark-border rounded-lg text-white text-sm focus:outline-none focus:border-primary transition-colors"
                      placeholder="votre@email.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="telephone" className="block text-sm font-medium text-gray-300 mb-2">
                      Téléphone *
                    </label>
                    <input
                      type="tel"
                      id="telephone"
                      name="telephone"
                      value={formData.telephone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-dark-bg border border-dark-border rounded-lg text-white text-sm focus:outline-none focus:border-primary transition-colors"
                      placeholder="+41 79 123 45 67"
                    />
                  </div>
                </div>
              </div>

              {/* Adresse */}
              <div>
                <h2 className="text-2xl font-bold text-primary mb-6">Votre adresse</h2>
                <div className="space-y-6">
                  <div>
                    <label htmlFor="adresse" className="block text-sm font-medium text-gray-300 mb-2">
                      Adresse complète *
                    </label>
                    <input
                      type="text"
                      id="adresse"
                      name="adresse"
                      value={formData.adresse}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-dark-bg border border-dark-border rounded-lg text-white text-sm focus:outline-none focus:border-primary transition-colors"
                      placeholder="Rue et numéro"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="codePostal" className="block text-sm font-medium text-gray-300 mb-2">
                        Code postal *
                      </label>
                      <input
                        type="text"
                        id="codePostal"
                        name="codePostal"
                        value={formData.codePostal}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-dark-bg border border-dark-border rounded-lg text-white text-sm focus:outline-none focus:border-primary transition-colors"
                        placeholder="1530"
                      />
                    </div>

                    <div>
                      <label htmlFor="ville" className="block text-sm font-medium text-gray-300 mb-2">
                        Ville *
                      </label>
                      <input
                        type="text"
                        id="ville"
                        name="ville"
                        value={formData.ville}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-dark-bg border border-dark-border rounded-lg text-white text-sm focus:outline-none focus:border-primary transition-colors"
                        placeholder="Payerne"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Véhicule */}
              <div>
                <h2 className="text-2xl font-bold text-primary mb-6">Votre véhicule</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label htmlFor="typeVehicule" className="block text-sm font-medium text-gray-300 mb-2">
                      Type de véhicule *
                    </label>
                    <select
                      id="typeVehicule"
                      name="typeVehicule"
                      value={formData.typeVehicule}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-dark-bg border border-dark-border rounded-lg text-white text-sm focus:outline-none focus:border-primary transition-colors cursor-pointer"
                    >
                      <option value="">Sélectionner</option>
                      <option value="Citadine">Citadine</option>
                      <option value="Berline">Berline</option>
                      <option value="SUV">SUV</option>
                      <option value="Break">Break</option>
                      <option value="Monospace">Monospace</option>
                      <option value="Sportive">Sportive</option>
                      <option value="Utilitaire">Utilitaire</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="marque" className="block text-sm font-medium text-gray-300 mb-2">
                      Marque *
                    </label>
                    <input
                      type="text"
                      id="marque"
                      name="marque"
                      value={formData.marque}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-dark-bg border border-dark-border rounded-lg text-white text-sm focus:outline-none focus:border-primary transition-colors"
                      placeholder="Ex: BMW"
                    />
                  </div>

                  <div>
                    <label htmlFor="modele" className="block text-sm font-medium text-gray-300 mb-2">
                      Modèle *
                    </label>
                    <input
                      type="text"
                      id="modele"
                      name="modele"
                      value={formData.modele}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-dark-bg border border-dark-border rounded-lg text-white text-sm focus:outline-none focus:border-primary transition-colors"
                      placeholder="Ex: X5"
                    />
                  </div>
                </div>
              </div>

              {/* Prestations souhaitées */}
              <div>
                <h2 className="text-2xl font-bold text-primary mb-6">Prestations souhaitées *</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {prestationsOptions.map((prestation) => (
                    <label
                      key={prestation}
                      className={`flex items-center p-4 bg-dark-bg border rounded-lg cursor-pointer transition-all ${
                        formData.prestations.includes(prestation)
                          ? 'border-primary bg-primary/5'
                          : 'border-dark-border hover:border-primary/50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.prestations.includes(prestation)}
                        onChange={() => handlePrestationToggle(prestation)}
                        className="w-5 h-5 text-primary bg-dark-bg border-dark-border rounded focus:ring-primary focus:ring-2 cursor-pointer"
                      />
                      <span className="ml-3 text-sm text-gray-300">{prestation}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Date et heure préférées */}
              <div>
                <h2 className="text-2xl font-bold text-primary mb-6">Date et heure préférées</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="datePreferee" className="block text-sm font-medium text-gray-300 mb-2">
                      Date préférée
                    </label>
                    <input
                      type="date"
                      id="datePreferee"
                      name="datePreferee"
                      value={formData.datePreferee}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-dark-bg border border-dark-border rounded-lg text-white text-sm focus:outline-none focus:border-primary transition-colors cursor-pointer"
                    />
                  </div>

                  <div>
                    <label htmlFor="heurePreferee" className="block text-sm font-medium text-gray-300 mb-2">
                      Heure préférée
                    </label>
                    <select
                      id="heurePreferee"
                      name="heurePreferee"
                      value={formData.heurePreferee}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-dark-bg border border-dark-border rounded-lg text-white text-sm focus:outline-none focus:border-primary transition-colors cursor-pointer"
                    >
                      <option value="">Sélectionner</option>
                      <option value="Matin (8h-12h)">Matin (8h-12h)</option>
                      <option value="Après-midi (12h-17h)">Après-midi (12h-17h)</option>
                      <option value="Soir (17h-20h)">Soir (17h-20h)</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                  Message ou demandes spécifiques
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-3 bg-dark-bg border border-dark-border rounded-lg text-white text-sm focus:outline-none focus:border-primary transition-colors resize-none"
                  placeholder="Décrivez vos besoins spécifiques, l'état du véhicule, ou toute autre information utile... (500 caractères max)"
                ></textarea>
                <p className="text-xs text-gray-500 mt-2">{formData.message.length}/500 caractères</p>
              </div>

              {/* Submit Button */}
              <div className="pt-6">
                <button
                  type="submit"
                  disabled={isSubmitting || formData.prestations.length === 0}
                  className="w-full px-8 py-4 bg-primary text-dark-bg rounded-lg font-semibold text-lg hover:bg-primary/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Envoi en cours...
                    </span>
                  ) : (
                    'Envoyer ma demande de devis'
                  )}
                </button>
              </div>

              {/* Status Messages */}
              {submitStatus === 'success' && (
                <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <div className="flex items-center text-green-400">
                    <i className="ri-checkbox-circle-line text-2xl mr-3"></i>
                    <div>
                      <p className="font-semibold">Demande envoyée avec succès !</p>
                      <p className="text-sm text-green-300 mt-1">
                        Nous vous contacterons sous 24h pour établir votre devis personnalisé.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <div className="flex items-center text-red-400">
                    <i className="ri-error-warning-line text-2xl mr-3"></i>
                    <div>
                      <p className="font-semibold">Erreur lors de l'envoi</p>
                      <p className="text-sm text-red-300 mt-1">
                        Veuillez réessayer ou nous contacter directement par téléphone.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>

          {/* Contact Info */}
          <div className="mt-12 text-center">
            <p className="text-gray-400 mb-4">Vous préférez nous appeler directement ?</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="tel:+41796840588"
                className="flex items-center space-x-2 text-primary hover:text-primary/80 transition-colors"
              >
                <i className="ri-phone-line text-xl"></i>
                <span className="font-semibold">+41 79 684 05 88</span>
              </a>
              <span className="hidden sm:block text-gray-600">|</span>
              <a
                href="https://wa.me/41796840588"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 text-green-500 hover:text-green-400 transition-colors"
              >
                <i className="ri-whatsapp-line text-xl"></i>
                <span className="font-semibold">WhatsApp</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DevisPage;
